/**
 * ReportHistorySidebar Component
 *
 * Sidebar showing upload history with clickable items to load reports.
 * Shows filename, date, event count, and delete option.
 */

import React, { useState, useEffect } from 'react';
import type { UploadHistory, PollerStatus } from '../hmcEventsStore';
import { useHMCEventsStore } from '../hmcEventsStore';

// Icons
const DocumentIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const TicketIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
  </svg>
);

const MailIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

export interface ReportStats {
  total: number;
  dynamo: number;
  dash: number;
  rockets: number;
  enriched: number;
  failed: number;
  selected: number;
  totalTickets: number;
  eventsWithTickets: number;
}

interface ReportHistorySidebarProps {
  history: UploadHistory[];
  currentUploadId?: string;
  onSelect: (uploadId: string) => void;
  loading?: boolean;
  stats?: ReportStats;
}

export const ReportHistorySidebar: React.FC<ReportHistorySidebarProps> = ({
  history,
  currentUploadId,
  onSelect,
  loading = false,
  stats,
}) => {
  const { deleteReport, pollerStatus, fetchPollerStatus } = useHMCEventsStore();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Poll for Gmail poller status every 5 seconds when component is mounted
  useEffect(() => {
    // Initial fetch
    fetchPollerStatus();

    // Poll every 5 seconds
    const interval = setInterval(() => {
      fetchPollerStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchPollerStatus]);

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle delete
  const handleDelete = async (uploadId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (deleteConfirmId === uploadId) {
      await deleteReport(uploadId);
      setDeleteConfirmId(null);
    } else {
      setDeleteConfirmId(uploadId);
      // Auto-cancel confirmation after 3 seconds
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="report-history-sidebar">
      {/* Header - hidden on mobile (shown in parent) */}
      <div className="hidden lg:block shrink-0 p-4 border-b border-dark-border/30">
        <h2 className="text-lg font-semibold text-white">Report History</h2>
        <p className="text-sm text-dark-muted mt-1">{history.length} reports</p>
      </div>

      {/* Gmail Processing Indicator */}
      {pollerStatus?.is_polling && (
        <div className="shrink-0 p-3 bg-brand-500/10 border-b border-brand-500/30">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-500 border-t-transparent"></div>
            <div className="flex items-center gap-1.5 text-brand-400">
              <MailIcon />
              <span className="text-sm font-medium">Processing email...</span>
            </div>
          </div>
          <p className="text-xs text-dark-muted mt-1 ml-6">
            New report will appear when complete
          </p>
        </div>
      )}

      {/* Loading state */}
      {loading && history.length === 0 && (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        </div>
      )}

      {/* Empty state */}
      {!loading && history.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <DocumentIcon />
          <p className="text-dark-muted mt-2">No reports yet</p>
          <p className="text-xs text-dark-muted mt-1">
            Upload a calendar file to get started
          </p>
        </div>
      )}

      {/* Report list - scrollable area */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {history.map((report) => (
          <div
            key={report.upload_id}
            onClick={() => onSelect(report.upload_id)}
            className={`
              p-4 border-b border-dark-border/30 cursor-pointer
              transition-all duration-150
              ${
                currentUploadId === report.upload_id
                  ? 'bg-brand-500/10 border-l-2 border-l-brand-500'
                  : 'hover:bg-dark-surface/50'
              }
            `}
            data-testid={`report-history-item-${report.upload_id}`}
          >
            {/* Filename */}
            <div className="flex items-start justify-between gap-2">
              <h3
                className={`
                  text-sm font-medium truncate flex-1
                  ${currentUploadId === report.upload_id ? 'text-brand-400' : 'text-white'}
                `}
                title={report.filename}
              >
                {report.filename}
              </h3>

              {/* Delete button */}
              <button
                onClick={(e) => handleDelete(report.upload_id, e)}
                className={`
                  p-1.5 rounded transition-all duration-150 flex-shrink-0
                  ${
                    deleteConfirmId === report.upload_id
                      ? 'bg-red-500 text-white'
                      : 'text-dark-muted hover:text-red-400 hover:bg-red-500/10'
                  }
                `}
                title={deleteConfirmId === report.upload_id ? 'Click again to confirm' : 'Delete report'}
              >
                <TrashIcon />
              </button>
            </div>

            {/* Meta info */}
            <div className="flex items-center gap-3 mt-2 text-xs text-dark-muted">
              <div className="flex items-center gap-1">
                <CalendarIcon />
                <span>{formatDate(report.created_at)}</span>
              </div>

              <span className="text-dark-muted">|</span>

              <span>{report.events_created} events</span>

              {report.file_size > 0 && (
                <>
                  <span className="text-dark-muted">|</span>
                  <span>{formatFileSize(report.file_size)}</span>
                </>
              )}
            </div>

            {/* Enrichment status */}
            <div className="mt-2">
              <span
                className={`
                  inline-block px-2 py-0.5 rounded text-xs font-medium
                  ${
                    report.enrichment_status === 'completed'
                      ? 'bg-green-500/20 text-green-400'
                      : report.enrichment_status === 'pending'
                      ? 'bg-yellow-500/20 text-yellow-400'
                      : 'bg-red-500/20 text-red-400'
                  }
                `}
              >
                {report.enrichment_status}
              </span>

              {report.error_message && (
                <p className="mt-1 text-xs text-red-400 truncate" title={report.error_message}>
                  {report.error_message}
                </p>
              )}
            </div>

            {/* Delete confirmation text */}
            {deleteConfirmId === report.upload_id && (
              <p className="mt-2 text-xs text-red-400">
                Click again to delete
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Stats Section - fixed at bottom, never scrolls */}
      {stats && (
        <div className="shrink-0 p-4 border-t border-dark-border/30 bg-dark-surface/50">
          <h3 className="text-xs font-semibold text-dark-muted uppercase tracking-wider mb-3">
            Current Report Stats
          </h3>

          {/* Total and Tickets */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-dark-muted">Total Events</span>
              <span className="text-sm font-semibold text-white">{stats.total}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-sm text-dark-muted">
                <TicketIcon />
                <span>Tickets</span>
              </div>
              <span className="text-sm font-semibold text-brand-400">{stats.totalTickets}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-dark-muted">Events w/ Tickets</span>
              <span className="text-sm text-brand-400">{stats.eventsWithTickets}</span>
            </div>
          </div>

          {/* Team breakdown */}
          <div className="mt-3 pt-3 border-t border-dark-border/30 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                <span className="text-sm text-dark-muted">Dynamo</span>
              </div>
              <span className="text-sm text-orange-400">{stats.dynamo}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-sky-500 rounded-full"></span>
                <span className="text-sm text-dark-muted">Dash</span>
              </div>
              <span className="text-sm text-sky-400">{stats.dash}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                <span className="text-sm text-dark-muted">Rockets</span>
              </div>
              <span className="text-sm text-red-400">{stats.rockets}</span>
            </div>
          </div>

          {/* Enrichment stats */}
          {(stats.enriched > 0 || stats.selected > 0) && (
            <div className="mt-3 pt-3 border-t border-dark-border/30 space-y-2">
              {stats.enriched > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-dark-muted">Enriched</span>
                  <span className="text-sm text-green-400">{stats.enriched}</span>
                </div>
              )}
              {stats.selected > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-dark-muted">Selected</span>
                  <span className="text-sm font-medium text-brand-400">{stats.selected}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Footer - fixed at bottom */}
      <div className="shrink-0 p-3 border-t border-dark-border/30 bg-dark-bg/50">
        <p className="text-xs text-dark-muted text-center">
          Reports stored in PostgreSQL
        </p>
      </div>
    </div>
  );
};

export default ReportHistorySidebar;
